import { CurrencyPipe, formatNumber, getNumberOfCurrencyDigits } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

/**
 * Price of a dish or of a price option as the menu shows it: Angular's CurrencyPipe with the narrow symbol, in the
 * active language. A price with cents shows all the decimals of its currency ("12,50 €", "$12.50", never "12,5 €"),
 * a whole price none ("12 €"), and a currency without decimals (JPY, KRW...) never any.
 *
 * Usage: {{ item.price | menuPrice:establishment.currencyCode:currentLocale }}
 */
@Pipe({
    name: 'menuPrice',
    standalone: true,
})
export class MenuPricePipe implements PipeTransform {
    private readonly _currencyPipe: CurrencyPipe;

    constructor(@Inject(LOCALE_ID) locale: string, @Inject(DEFAULT_CURRENCY_CODE) defaultCurrencyCode: string) {
        this._currencyPipe = new CurrencyPipe(locale, defaultCurrencyCode);
    }

    transform(value: number | string | null | undefined, currencyCode: string, locale?: string): string | null {
        return this._currencyPipe.transform(value, currencyCode, 'symbol-narrow', menuPriceDigitsInfo(value, currencyCode), locale);
    }
}

/**
 * digitsInfo of a menu price: '1.0' (no decimals) for a whole price, all the decimals of the currency otherwise
 * ('1.2-2' for EUR or USD). "Whole" is decided with the CurrencyPipe's own rounding (half up on the decimal digits, at
 * the currency's precision), so that a price shows decimals exactly when they are not all zeros.
 */
export function menuPriceDigitsInfo(value: number | string | null | undefined, currencyCode: string): string {
    const digits = getNumberOfCurrencyDigits(currencyCode);
    const amount = typeof value === 'string' ? Number(value) : value;
    if (digits === 0 || amount === null || amount === undefined || !Number.isFinite(amount)) {
        return '1.0';
    }
    // en-US: always available, '.' as decimal separator and ',' to group
    return formatNumber(amount, 'en-US', `1.0-${digits}`).includes('.') ? `1.${digits}-${digits}` : '1.0';
}
